import { z } from 'zod';
import { getData, patchData, postData } from '~/utils/service';
import { JobMessageSchema, JobOpportunitySchema } from './schemas';
import type { JobMessage, JobOpportunity, OpportunityFilter, OpportunityStatus } from './types';

const JobOpportunitiesResponseSchema = z.object({
  data: z.array(JobOpportunitySchema),
  totalCount: z.number().int().nonnegative(),
});
const LegacyJobOpportunitiesResponseSchema = z.array(JobOpportunitySchema);
const CompatibleJobOpportunitiesResponseSchema = z.union([
  JobOpportunitiesResponseSchema,
  LegacyJobOpportunitiesResponseSchema,
]);

export const API_CALLS = {
  getOpportunities: async (
    filter: OpportunityFilter = 'all',
    signal?: AbortSignal,
    includeDeclined = false
  ): Promise<{ items: JobOpportunity[]; totalCount: number }> => {
    const data = await getData('jobOpportunities', {
      signal,
      params: { status: filter, include_total: true, includeDeclined: String(includeDeclined) },
    });
    const parsed = CompatibleJobOpportunitiesResponseSchema.parse(data);
    if (Array.isArray(parsed)) {
      return { items: parsed, totalCount: parsed.length };
    }
    return { items: parsed.data, totalCount: parsed.totalCount };
  },

  getMessages: async (responseId: number, signal?: AbortSignal): Promise<JobMessage[]> => {
    const data = await getData('jobMessages', {
      signal,
      pathParams: { responseId: responseId.toString() },
    });
    return z
      .array(JobMessageSchema.refine((message) => message.responseId === responseId))
      .parse(data);
  },

  sendMessage: async (
    responseId: number,
    messageBody: string,
    signal?: AbortSignal
  ): Promise<JobMessage> => {
    const data = await postData('jobMessages', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { messageBody },
    });
    return JobMessageSchema.refine((message) => message.responseId === responseId).parse(data);
  },

  updateStatus: async (
    responseId: number,
    status: OpportunityStatus,
    signal?: AbortSignal,
    expectedNegotiationId?: number
  ): Promise<void> => {
    if (status === 'ACCEPTED') {
      // Bilateral acceptance uses dedicated POST /accept endpoint
      await postData('hlxAccept', {
        signal,
        pathParams: { responseId: responseId.toString() },
        data: { expectedNegotiationId },
      });
    } else {
      await patchData('jobOpportunityStatus', {
        signal,
        pathParams: { responseId: responseId.toString() },
        data: { status },
      });
    }
  },
};
